// src/pages/MinistereEAC.jsx
import React, { useState, useEffect } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Card,
  Divider,
  Box,
  CardMedia,
  Button,
  styled,
  createTheme,
  ThemeProvider
} from '@mui/material';
import moment from 'moment';
import { getPublications } from '../services/publicationService';

function MinistereEAC() {
  const [visibleCount, setVisibleCount] = useState(2);
  const [statistiquesData, setStatistiquesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'amae', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            annuaires: item.title,
            link: item.document_url,
            image: item.image_url, // si l'API fournit une image, on l'utilise ; sinon placeholder
            description: item.description,
            date: new Date(item.date),
          }));
          // Trier par date décroissante (plus récent en premier)
          const sorted = mapped.sort((a, b) => b.date - a.date);
          setStatistiquesData(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => Math.min(prevCount + 2, statistiquesData.length));
  };

  const theme = createTheme({
    palette: {
      primary: { main: '#388E3C' },
      background: { default: '#f5f5f5' }
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      body1: {
        fontSize: 16,
        letterSpacing: 0.5,
        color: '#6B778C'
      },
      h4: { fontWeight: '600' }
    }
  });

  const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '20px'
  }));

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
              Ministère des Affaires Etrangères, de l'Intégration Régionale et de la Coopération au Développement
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
              Ministère des Affaires Etrangères, de l'Intégration Régionale et de la Coopération au Développement
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
            Ministère des Affaires Etrangères, de l'Intégration Régionale et de la Coopération au Développement
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Les statistiques sur les initiatives menées par le Ministère dans le cadre de la coopération régionale, la promotion de la jeunesse, des sports et de la culture au sein de la Communauté Est-Africaine.
          </Typography>

          {statistiquesData.slice(0, visibleCount).map((statistique, index) => (
            <StyledCard key={index} elevation={3}>
              <Box sx={{ display: 'flex' }}>
                <a 
                  href={statistique.link} 
                  style={{ textDecoration: 'none', color: 'inherit' }} 
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <CardMedia
                    component="img"
                    sx={{ width: 71, height: 100, m: 2 }}
                    image={statistique.image || "path/to/image_placeholder.jpg"} // fallback si pas d'image
                    alt={statistique.annuaires}
                  />
                </a>
                <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                  <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: theme.palette.primary.main }}>
                    <a 
                      href={statistique.link} 
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <DownloadIcon /> {statistique.annuaires}
                    </a>
                  </Typography>
                  <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                    {statistique.description}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Publié en {moment(statistique.date).format('MMMM YYYY')}
                  </Typography>
                </Box>
              </Box>
            </StyledCard>
          ))}

          {visibleCount < statistiquesData.length && (
            <Button 
              onClick={handleShowMore} 
              style={{
                marginTop: '20px',
                backgroundColor: theme.palette.primary.main,
                color: 'white'
              }}
            >
              Plus de Statistiques
            </Button>
          )}
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default MinistereEAC;